import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import InventoryForm from '../components/inventory/InventoryForm';
import moment from 'moment';

export default function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { inventory, inventoryTransactions, addInventoryTransaction, updateInventoryItem, jobs } = useData();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactionType, setTransactionType] = useState('in');
  const [transactionQuantity, setTransactionQuantity] = useState(0);
  const [transactionNotes, setTransactionNotes] = useState('');

  const item = inventory.find(i => i.id === id);

  if (!item) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Item not found</Typography>
        <Button onClick={() => navigate('/inventory')} sx={{ mt: 2 }}>
          Back to Inventory
        </Button>
      </Box>
    );
  }

  const totalQty = item.totalQuantity || item.quantity || 0;
  const checkedOut = item.checkedOutQuantity || 0;
  const available = totalQty - checkedOut;

  const itemTransactions = inventoryTransactions.filter(t => t.itemId === id);
  const checkoutTransactions = itemTransactions.filter(t => t.type === 'checkout');
  const checkinTransactions = itemTransactions.filter(t => t.type === 'checkin');
  
  // Get currently checked out items by job
  const checkedOutByJob = {};
  checkoutTransactions.forEach(t => {
    if (!checkedOutByJob[t.jobId]) {
      checkedOutByJob[t.jobId] = 0;
    }
    checkedOutByJob[t.jobId] += t.quantity || 0;
  });
  checkinTransactions.forEach(t => {
    if (checkedOutByJob[t.jobId]) {
      checkedOutByJob[t.jobId] -= t.quantity || 0;
    }
  });
  
  const currentlyCheckedOut = Object.entries(checkedOutByJob)
    .filter(([jobId, qty]) => qty > 0)
    .map(([jobId, qty]) => ({ jobId, quantity: qty }));

  const isLowStock = available <= (item.lowStockThreshold || 10);
  const isOutOfStock = available === 0;

  const handleTransaction = async () => {
    if (transactionQuantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    if (transactionType === 'out' && transactionQuantity > item.quantity) {
      alert('Insufficient stock');
      return;
    }

    try {
      await addInventoryTransaction({
        itemId: id,
        type: transactionType,
        quantity: transactionQuantity,
        notes: transactionNotes,
        createdBy: 'Admin'
      });
      setTransactionDialogOpen(false);
      setTransactionQuantity(0);
      setTransactionNotes('');
    } catch (error) {
      console.error('Error adding transaction:', error);
      alert('Failed to process transaction');
    }
  };

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'in': return 'Stock In';
      case 'out': return 'Stock Out';
      case 'adjust': return 'Adjustment';
      default: return type;
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'in': return 'success';
      case 'out': return 'error';
      case 'adjust': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/inventory')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{item.name}</Typography>
        <IconButton
          color="primary"
          onClick={() => setEditDialogOpen(true)}
        >
          <EditIcon />
        </IconButton>
      </Box>

          {(isLowStock || isOutOfStock) && (
        <Alert
          severity={isOutOfStock ? 'error' : 'warning'}
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          {isOutOfStock
            ? 'This item is out of stock!'
            : `Low stock warning: Only ${available} ${item.unit || 'units'} available`}
        </Alert>
      )}

      {/* Currently Checked Out to Jobs */}
      {currentlyCheckedOut.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Currently Checked Out to Jobs
          </Typography>
          {currentlyCheckedOut.map(({ jobId, quantity }) => {
            const job = jobs.find(j => j.id === jobId);
            return (
              <Box key={jobId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    {job ? job.name || job.jobId : `Job ${jobId}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {quantity} {item.unit || 'units'} checked out
                  </Typography>
                </Box>
                {job && (
                  <Button size="small" onClick={() => navigate(`/jobs/${jobId}`)}>
                    View Job
                  </Button>
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Item Details
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">SKU</Typography>
                <Typography variant="body1">{item.sku || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Category</Typography>
                <Chip label={item.category} size="small" sx={{ mt: 0.5 }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">Total Quantity</Typography>
                <Typography variant="h5" sx={{ mt: 0.5 }}>
                  {totalQty} {item.unit || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">Available</Typography>
                <Typography variant="h5" sx={{ mt: 0.5, color: 'success.main' }}>
                  {available} {item.unit || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">Checked Out</Typography>
                <Typography variant="h5" sx={{ mt: 0.5, color: 'warning.main' }}>
                  {checkedOut} {item.unit || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Low Stock Threshold</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {item.lowStockThreshold || 10} {item.unit || 'units'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Unit Price</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {item.unitPrice ? `$${item.unitPrice.toFixed(2)}` : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Total Value</Typography>
                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                  ${(totalQty * (item.unitPrice || 0)).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Location</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {item.location || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Supplier</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {item.supplier || '-'}
                </Typography>
              </Grid>
              {item.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {item.description}
                  </Typography>
                </Grid>
              )}
              {item.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {item.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Transaction History</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setTransactionType('in');
                  setTransactionDialogOpen(true);
                }}
                size="small"
              >
                Add Transaction
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Quantity</strong></TableCell>
                    <TableCell><strong>Previous</strong></TableCell>
                    <TableCell><strong>New</strong></TableCell>
                    <TableCell><strong>Notes</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No transactions yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {moment(transaction.createdAt).format('MMM D, YYYY h:mm A')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTransactionTypeLabel(transaction.type)}
                            color={getTransactionTypeColor(transaction.type)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {transaction.type === 'out' ? '-' : '+'}
                          {transaction.quantity} {item.unit || ''}
                        </TableCell>
                        <TableCell>{transaction.previousQuantity || 0}</TableCell>
                        <TableCell><strong>{transaction.newQuantity || 0}</strong></TableCell>
                        <TableCell>{transaction.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => {
                  setTransactionType('in');
                  setTransactionDialogOpen(true);
                }}
                fullWidth
              >
                Stock In
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<RemoveIcon />}
                onClick={() => {
                  setTransactionType('out');
                  setTransactionDialogOpen(true);
                }}
                fullWidth
                disabled={item.quantity === 0}
              >
                Stock Out
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setTransactionType('adjust');
                  setTransactionDialogOpen(true);
                }}
                fullWidth
              >
                Adjust Quantity
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <InventoryForm
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        item={item}
      />

      <Dialog
        open={transactionDialogOpen}
        onClose={() => {
          setTransactionDialogOpen(false);
          setTransactionQuantity(0);
          setTransactionNotes('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {transactionType === 'in' && 'Stock In'}
          {transactionType === 'out' && 'Stock Out'}
          {transactionType === 'adjust' && 'Adjust Quantity'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={transactionQuantity}
              onChange={(e) => setTransactionQuantity(parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
            <TextField
              fullWidth
              label="Notes"
              value={transactionNotes}
              onChange={(e) => setTransactionNotes(e.target.value)}
              multiline
              rows={3}
            />
            {transactionType === 'out' && transactionQuantity > available && (
              <Alert severity="error">
                Insufficient stock. Available: {available} {item.unit || 'units'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setTransactionDialogOpen(false);
              setTransactionQuantity(0);
              setTransactionNotes('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTransaction}
            variant="contained"
            disabled={transactionQuantity <= 0 || (transactionType === 'out' && transactionQuantity > available)}
          >
            {transactionType === 'in' && 'Add Stock'}
            {transactionType === 'out' && 'Remove Stock'}
            {transactionType === 'adjust' && 'Adjust'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

